"use client"
import {
  FlexCenter,
  Modal,
  ModalBody,
  ModalFooter,
  ModalFooterButton,
  ModalHeader,
  ModalHeaderTitle,
  type ModalProps,
  NumberDisplay,
  TextBody,
  TextDisplay,
  VisuallyHidden,
} from "@opensea/ui-kit"
import { Error } from "@opensea/ui-kit/icons"
import { bn } from "@opensea/utils"
import type { FragmentOf } from "gql.tada"
import { readFragment } from "gql.tada"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import type { CreateOfferFlowFormValues } from "../../types"
import { CreateOfferPriceWarningModal_itemFragment } from "./CreateOfferPriceWarningModal.fragment"

type CreateOfferPriceWarningModal = Pick<ModalProps, "open"> & {
  item: FragmentOf<typeof CreateOfferPriceWarningModal_itemFragment>
  setIsOpen: (isOpen: boolean) => void
  onNext: () => void
}

export const CreateOfferPriceWarningModal = ({
  item: itemKey,
  open,
  onNext,
  setIsOpen,
}: CreateOfferPriceWarningModal) => {
  const t = useTranslations("wallet.CreateOfferPriceWarningModal")
  const { control } = useFormContext<CreateOfferFlowFormValues>()
  const offers = useWatch({ control, name: "offers" })
  const highSevPriceWarnings = offers.filter(offer => offer?.shouldConfirmPrice)
  const item = readFragment(CreateOfferPriceWarningModal_itemFragment, itemKey)
  const offerSymbol = item.chain.offerCurrency.symbol
  const listingSymbol = item.chain.listingCurrency.symbol
  const bestListing = item.bestListing
  const bestOffer = item.bestOffer

  if (highSevPriceWarnings.length === 0) {
    return null
  }

  const firstWarningOffer = highSevPriceWarnings[0]
  const offerPriceIsAboveBestListing =
    bestListing?.pricePerItem.token.unit &&
    firstWarningOffer?.price &&
    bn(firstWarningOffer.price).gt(bn(bestListing.pricePerItem.token.unit))

  return (
    <Modal
      content={
        <>
          <ModalHeader>
            <VisuallyHidden>
              <ModalHeaderTitle>{t("title")}</ModalHeaderTitle>
            </VisuallyHidden>
          </ModalHeader>
          <ModalBody className="flex-col items-center gap-3">
            <FlexCenter className="rounded-full bg-error-1-transparent-1 p-3">
              <Error fill="error-1" size={28} />
            </FlexCenter>
            <TextDisplay size="sm">{t("highOfferPrice")}</TextDisplay>
            <TextBody className="text-center text-secondary" size="md">
              {offerPriceIsAboveBestListing
                ? t.rich("warningMessageListing", {
                    price: () => (
                      <NumberDisplay
                        className="text-text-primary"
                        suffix={offerSymbol}
                        value={firstWarningOffer?.price}
                      />
                    ),
                    bestListing: () => (
                      <NumberDisplay
                        className="text-text-primary"
                        suffix={listingSymbol}
                        value={bn(bestListing.pricePerItem.token.unit)}
                      />
                    ),
                  })
                : t.rich("warningMessageOffer", {
                    price: () => (
                      <NumberDisplay
                        className="text-text-primary"
                        suffix={offerSymbol}
                        value={firstWarningOffer?.price}
                      />
                    ),
                    bestOffer: () => (
                      <NumberDisplay
                        className="text-text-primary"
                        suffix={offerSymbol}
                        value={
                          bestOffer && bn(bestOffer.pricePerItem.token.unit)
                        }
                      />
                    ),
                  })}
            </TextBody>
          </ModalBody>
          <ModalFooter>
            <ModalFooterButton
              onClick={() => {
                setIsOpen(false)
              }}
              variant="secondary"
            >
              {t("cancel")}
            </ModalFooterButton>
            <ModalFooterButton
              onClick={() => {
                setIsOpen(false)
                onNext()
              }}
            >
              {t("confirmOfferPrice")}
            </ModalFooterButton>
          </ModalFooter>
        </>
      }
      onOpenChange={setIsOpen}
      open={open}
    />
  )
}
